<script lang="ts">
	import { onMount } from 'svelte';
	import { createForm } from 'svelte-forms-lib';
	import Textfield from '@smui/textfield/styled';
	import SmuiButton, { Label as SmuiLabel, Icon as ButtonIcon } from '@smui/button/styled';
	import * as yup from 'yup';
	import EstimatedUsers from './EstimatedUsers.svelte';
	import Dialog, { Title, Content, Actions } from '@smui/dialog/styled';
	import { Link } from 'svelte-routing';
	import { APP_PATHS } from '../../../constants';
	import { graphQLErrors as graphQLErrorsStore, userClientData, userInfo } from '../../../stores';
	import { getClient, mutate, query } from 'svelte-apollo';
	import AppContainer from '../../AppContainer.svelte';
	import Text from '../../Text.svelte';
	import Card from '../../Card.svelte';
	import HorizontalDnD from '../HorizontalDnD.svelte';
	import CardRow from '../../CardRow.svelte';
	import GraphQLErrorRender from '../../GraphQL/GraphQLErrorRender.svelte';
	import Button from '../../Button.svelte';
	import {
		createMixedSegment,
		importSegmentToPlatform,
		getLastSegmentQuery
	} from '../../../graphql/queries/segments';
	import type { DocumentNode } from 'graphql';
	import type { ApolloQueryResult } from 'apollo-boost';

	export let data = [];
	export let isDentsu: boolean;

	let queryString = '';

	let logicalOperators = [
		{ id: 1000, name: 'and', type: 1 },
		{ id: 1001, name: 'or', type: 1 },
		{ id: 1002, name: '()', type: 1 },
		{ id: 1003, name: 'not()', type: 1 }
	];
	let segments = [];
	let build = [];
	const client = getClient();

	const getLastSegmentOp = query(client, {
		query: getLastSegmentQuery
	});

	let okDialog = false;
	let errorDialog = false;
	let errorMessage: string;
	let sendToPlatform = false;
	let selected_CZ_SK = 'CZ';
	let dentsuCzMixSegmentCost = 0.65;
	let okPlatformDialog = false;
	let isSaved: boolean;
	let isPlatformSaved: boolean;
	let segmentIdForPlatform = '';
	let lastSegmentId = 0;
	let defaultFee = '0';
	let traitID = '';

	function handleError(errMsg: string) {
		errorMessage = errMsg;
		errorDialog = true;
		console.error('Error deleting segment', errMsg);
	}

	const createSegmentOp = (values: any) => {
		const { personaName } = values;
		return new Promise((resolve) =>
			mutate(client, {
				mutation: createMixedSegment,
				variables: {
					name: $userClientData.webData.nameFunc(
						personaName,
						$userInfo.client.namePostfix,
						'',
						'',
						[],
						'',
						[],
						[],
						[],
						[],
						lastSegmentId,
						false,
						true
					),
					combined: JSON.stringify(queryString.trim().split(' ')),
					fee: defaultFee,
					sendToPlatform,
					external_id: traitID
				},
				errorPolicy: 'all'
			})
				.then((dataResponse) => {
					resolve(dataResponse);
					segmentIdForPlatform = atob(dataResponse.data.createMixedSegment.segment.id).replace(
						'MSegment:',
						''
					);
				})
				.catch((err) => {
					console.warn(err);
					resolve({ errors: err });
				})
		);
	};

	const { form, errors, isValid, handleChange, handleSubmit } = createForm({
		initialValues: {
			personaName: ''
		},
		validationSchema: yup.object().shape({
			personaName: yup.string().required().min(3).max(128)
		}),
		onSubmit: async (values) => {
			await getLastSegmentOp.refetch();
			getLastSegmentOp.subscribe(async (promise) => {
				await (promise as Promise<ApolloQueryResult<any>>).then((data) => {
					lastSegmentId = data.data.lsegment[0].segmentIntId;
				});
			});
			graphQLErrorsStore.set(undefined);
			return createSegmentOp(values)
				.then(() => {
					isSaved = true;
					okDialog = true;
				})
				.catch((err) => {
					handleError(err.message);
				});
		}
	});

	onMount(() => {
		getLastSegmentOp.refetch();
		getLastSegmentOp.subscribe(async (promise) => {
			await (promise as Promise<ApolloQueryResult<any>>).then((data) => {
				lastSegmentId = data.data.lsegment[0].segmentIntId;
			});
		});
		let newObj = [];
		data.forEach((element, index) =>
			newObj.push({ id: index, name: element.name, firstRun: element.firstRun })
		);
		segments = newObj.sort(function (a, b) {
			var nameA = a.name.toLowerCase(),
				nameB = b.name.toLowerCase();
			return nameA < nameB ? -1 : 1;
		});
	});

	function dndParseString(event: CustomEvent) {
		queryString = event.detail.text;
	}

	const toggleSegmentMutation = (mutation: DocumentNode) => {
		return new Promise((resolve) =>
			mutate(client, {
				mutation: mutation,
				variables: {
					seg_id: segmentIdForPlatform
				},
				errorPolicy: 'all'
			})
				.then((responseData) => {
					const { errors } = responseData;
					if (errors) {
						console.log(errors);
						handleError(errors[0].message);
					} else {
						isPlatformSaved = true;
						okPlatformDialog = true;
					}
				})
				.catch((err) => {
					handleError(err.message);
					resolve({ errors: err });
				})
		);
	};

	$: {
		if (selected_CZ_SK === 'CZ') {
			dentsuCzMixSegmentCost = 0.65;
		} else {
			dentsuCzMixSegmentCost = 0.6;
		}
	}
</script>

<AppContainer>
	<form class:valid={$isValid} on:submit={handleSubmit}>
		<Text gutter type="headline2">Mix your persona</Text>
		<Text gutter type="body1"
			>Please drag and drop your segments and add logical operators of your choice</Text
		>
		<div class="margin" />
		<Card noborder>
			<div class="row">
				<Text>Name:</Text>
				<div class="fullwidth">
					<Textfield
						invalid={$errors['personaName']}
						input$name="personaName"
						class="fullwidth"
						variant="outlined"
						value={$form.personaName}
						on:change={handleChange}
						on:blur={handleChange}
					/>
				</div>
				<div class="text-end">
					<Text>
						{$userClientData.webData.nameEnd}
					</Text>
				</div>
				{#if !$userClientData.webData.default}
					<SmuiButton
						variant="raised"
						type="button"
						on:click={() => {
							$form.personaName = $userClientData.webData.nameFunc(
								$form.personaName,
								$userInfo.client.namePostfix,
								'',
								'',
								[],
								[],
								[],
								[],
								[],
								[],
								lastSegmentId,
								true,
								true
							);
						}}>Generate name</SmuiButton
					>
				{/if}
			</div>
		</Card>
		<Card title={'Options segments'}>
			<HorizontalDnD items={segments} logical={false} hasfilter={true} />
		</Card>
		<Card title={'Logical operators'}>
			<HorizontalDnD items={logicalOperators} logical={true} />
		</Card>
		<Card title={'Mix builder'}>
			<HorizontalDnD items={build} logical={false} input={true} on:parseQuery={dndParseString} />
		</Card>
		{#if isDentsu}
			<CardRow>
				<Card noborder inline>
					<div class="fee">
						Default fee - {dentsuCzMixSegmentCost} €
					</div>
					<div class="row confirmation">
						<div>
							<SmuiButton type="submit" variant="raised" disabled={isSaved}>
								<ButtonIcon class="material-icons">save</ButtonIcon>
								<SmuiLabel>Save</SmuiLabel>
							</SmuiButton>
						</div>
						<div
							title={isSaved && !isPlatformSaved
								? ''
								: 'Sending data to ' +
								  $userInfo.client.platformLabel +
								  ' is possible only after saving the segment'}
						>
							<SmuiButton
								on:click={() => {
									toggleSegmentMutation(importSegmentToPlatform);
								}}
								disabled={!isSaved && !isPlatformSaved}
								type="button"
							>
								<SmuiLabel>Start sending data to {$userInfo.client.platformLabel}</SmuiLabel>
								<ButtonIcon class="material-icons">add</ButtonIcon>
							</SmuiButton>
						</div>
					</div>
				</Card>
				<Card inline flex class="est-card">
					<EstimatedUsers queryBuild={queryString} />
				</Card>
			</CardRow>
		{:else}
			<CardRow>
				<Card noborder inline>
					{#if $userClientData.webData.fee}
						<div class="row">
							<Text>Default fee</Text>
							<div class={`fullwidth`}>
								<Textfield
									input$name="defaultFee"
									bind:value={defaultFee}
									class="fullwidth"
									variant="outlined"
									withLeadingIcon
									type="number"
									input$step={0.01}
									input$placeholder="0.00"
									input$min={0}
								/>
							</div>
						</div>
						<div />
					{:else}
						<Text>If you want to replace existing segment, write its ID below:</Text>
						<div class="row">
							<div class={`fullwidth`}>
								<Textfield
									input$name="traitID"
									bind:value={traitID}
									class="fullwidth"
									withLeadingIcon
									type="text"
								/>
							</div>
						</div>
						<div />
					{/if}
					<div class="row confirmation">
						<div>
							<SmuiButton type="submit" variant="raised" disabled={isSaved}>
								<ButtonIcon class="material-icons">save</ButtonIcon>
								<SmuiLabel>Save</SmuiLabel>
							</SmuiButton>
						</div>
						<div
							title={isSaved && !isPlatformSaved
								? ''
								: 'Sending data to ' +
								  $userInfo.client.platformLabel +
								  ' is possible only after saving the segment'}
						>
							<SmuiButton
								on:click={() => {
									toggleSegmentMutation(importSegmentToPlatform);
								}}
								disabled={!isSaved && !isPlatformSaved}
								type="button"
							>
								<SmuiLabel>Start sending data to {$userInfo.client.platformLabel}</SmuiLabel>
								<ButtonIcon class="material-icons">add</ButtonIcon>
							</SmuiButton>
						</div>
					</div>
				</Card>
				<Card inline flex class="est-card">
					<EstimatedUsers queryBuild={queryString} />
				</Card>
			</CardRow>
		{/if}
		<GraphQLErrorRender />
	</form>
</AppContainer>

<Dialog
	open={okDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">New Mixed Segment created</Title>
	<Content id="dialog-content">
		New mixed segment was successfuly created and stored to database.
		<br />
		You can view it in
		<Link to={APP_PATHS.COMBINE_DASHBOARD}>Dashboard</Link>
	</Content>

	<Actions>
		<div style="width: 100%; display: flex; justify-content: space-between;">
			<SmuiButton
				on:click={() => {
					toggleSegmentMutation(importSegmentToPlatform);
				}}
				disabled={!isSaved && !isPlatformSaved}
				type="button"
			>
				<SmuiLabel>Start sending data to {$userInfo.client.platformLabel}</SmuiLabel>
				<ButtonIcon class="material-icons">add</ButtonIcon>
			</SmuiButton>
			<Button on:click={() => (okDialog = false)}>Close</Button>
		</div>
	</Actions>
</Dialog>

<Dialog
	open={errorDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">Error creating new mixed segment</Title>
	<Content id="dialog-content">{errorMessage}</Content>

	<Actions>
		<Button on:click={() => (errorDialog = false)}>Close</Button>
	</Actions>
</Dialog>

<Dialog
	open={okPlatformDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">Import done</Title>
	<Content id="dialog-content">
		The data will be sent to {$userInfo.client.platformLabel} from tomorrow.
		<br />
		You can view it in
		<Link to={APP_PATHS.DASHBOARD}>Dashboard</Link>
	</Content>

	<Actions>
		<Button on:click={() => (okPlatformDialog = false)}>Close</Button>
	</Actions>
</Dialog>

<style>
	:global(*) {
		box-sizing: border-box;
		margin: 0;
	}
	.margin {
		margin-top: 50px;
	}
	.row {
		display: flex;
		text-align: left;
		padding: 14px 0;
		flex-direction: column;
	}

	.row > :global(div:first-child) {
		padding-bottom: 12px;
	}

	.confirmation {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		padding: 50px 0 30px;
	}

	.confirmation div:first-child {
		padding-bottom: 0;
	}

	.confirmation :global(div:first-child button) {
		font-size: 20px;
		height: 56px;
		padding: 0 24px;
	}

	.confirmation :global(div:first-child .mdc-button__icon) {
		font-size: 20px;
		height: 20px;
		margin-right: 18px;
	}

	@media screen and (min-width: 600px) {
		.row {
			flex-direction: row;
			align-items: center;
		}
		.row > :global(div:first-child) {
			min-width: 110px;
			padding-bottom: 0;
		}
	}

	.text-end {
		margin-left: 20px;
		margin-right: 20px;
	}
</style>
